import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { StarPicker } from "./Stars";

/** Shown on Now the moment you cross 100%: rate, one line, join the finishers. */
export function FinishLineCard({ clubId, book, embedded }: { clubId: string; book: ClubBook; /** inside the finishing ceremony: no card or heading of its own */ embedded?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [stars, setStars] = useState<number | null>(null);
  const [reaction, setReaction] = useState("");
  const done = useMutation({
    mutationFn: () => api.finishLine(clubId, book.id, { stars, reaction: reaction.trim() || null }),
    onSuccess: async ({ finishLineId }) => {
      await api.dismissMoment(clubId, book.id).catch(() => undefined); // the ceremony was the moment
      await qc.invalidateQueries({ queryKey: ["books", clubId] });
      void navigate({ to: "/clubs/$clubId/talk", params: { clubId }, search: { book: book.id, thread: finishLineId } });
    },
  });
  return (
    <section className={embedded ? "finish-form stack-sm" : "card finish stack-sm"}>
      {!embedded && <div className="eyebrow">You finished</div>}
      {!embedded && <h2 className="book">{book.book.title}</h2>}
      <div className="row" style={{ gap: 10, flexWrap: "wrap" }}><span className="small muted">Your rating</span><StarPicker value={stars} onChange={setStars} /></div>
      <label className="field">
        <span>First reaction, one line. Only other finishers see it.</span>
        <input id="first-reaction" className="input" value={reaction} maxLength={280} placeholder="Say the thing you'd say closing the book" onChange={(e) => setReaction(e.target.value)} />
      </label>
      {done.isError && <p className="error">{describeError(done.error)}</p>}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <button className="btn primary" disabled={done.isPending} onClick={() => done.mutate()}>Join the finishers</button>
        <span className="muted small">Both optional. You can rate later on the Shelf.</span>
      </div>
    </section>
  );
}

/** Shown on Now when the club finished a book you were still reading. */
export function StragglerCard({ clubId, s }: { clubId: string; s: NonNullable<import("@pagemates/shared").ClubBooks["straggler"]> }) {
  const qc = useQueryClient();
  const [giving, setGiving] = useState(false);
  const [stars, setStars] = useState<number | null>(null);
  const [reaction, setReaction] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["books", clubId] });
  const keep = useMutation({ mutationFn: () => api.keepReading(clubId, s.clubBookId), onSuccess: refresh });
  const giveUp = useMutation({ mutationFn: () => api.giveUp(clubId, s.clubBookId, { stars, reaction: reaction.trim() || null }), onSuccess: refresh });
  return (
    <section className="card finish stack-sm">
      <div className="eyebrow">The club finished {s.title}</div>
      <p>You're at <strong className="num">{s.percent}%</strong>.</p>
      {!giving ? (
        <div className="stack-sm">
          <button className="btn" disabled={keep.isPending} onClick={() => keep.mutate()}><b>Keep reading</b><span className="muted small" style={{ marginLeft: 8 }}>nothing changes; the ending stays sealed until you get there</span></button>
          <button className="btn" onClick={() => setGiving(true)}><b>I'm done with it</b><span className="muted small" style={{ marginLeft: 8 }}>opens the whole discussion for you</span></button>
          <p className="muted small">The <Link to="/clubs/$clubId/recap/$bookId" params={{ clubId, bookId: s.clubBookId }}>recap</Link> is up either way.</p>
        </div>
      ) : (
        <div className="stack-sm">
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}><span className="small muted">Rating, if you want</span><StarPicker value={stars} onChange={setStars} /></div>
          <input id="dnf-reaction" className="input" value={reaction} maxLength={280} placeholder="A line on why, optional" onChange={(e) => setReaction(e.target.value)} />
          {giveUp.isError && <p className="error">{describeError(giveUp.error)}</p>}
          <div className="row"><button className="btn primary" disabled={giveUp.isPending} onClick={() => giveUp.mutate()}>Mark as done with</button><button className="btn ghost" onClick={() => setGiving(false)}>Back</button></div>
        </div>
      )}
      {keep.isError && <p className="error">{describeError(keep.error)}</p>}
    </section>
  );
}

/** "I'm done with it" from your own progress row, any time. */
export function GiveUpInline({ clubId, book, onDone }: { clubId: string; book: ClubBook; onDone: () => void }) {
  const qc = useQueryClient();
  const [stars, setStars] = useState<number | null>(null);
  const [reaction, setReaction] = useState("");
  const giveUp = useMutation({
    mutationFn: () => api.giveUp(clubId, book.id, { stars, reaction: reaction.trim() || null }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); onDone(); },
  });
  return (
    <div className="editor">
      <p className="small">Done with <b>{book.book.title}</b>? You'll be marked "didn't finish", the whole discussion opens for you, and your rating stays out of the club average.</p>
      <div className="inline"><span>Rating, if you want</span><StarPicker value={stars} onChange={setStars} /></div>
      <input id="dnf-inline" className="input" value={reaction} maxLength={280} placeholder="A line on why, optional" onChange={(e) => setReaction(e.target.value)} />
      {giveUp.isError && <p className="error">{describeError(giveUp.error)}</p>}
      <div className="inline spread"><button type="button" className="btn ghost sm" onClick={onDone}>Cancel</button><button type="button" className="btn danger sm" disabled={giveUp.isPending} onClick={() => giveUp.mutate()}>I'm done with it</button></div>
    </div>
  );
}
